import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import FrontechLogo from './FrontechLogo';
import { CreditCard, Check, Zap, ArrowRight, Star, MessageSquare, Mic, DollarSign } from 'lucide-react';

const PLAN_ACCENT = {
  free:       '#5B7FA6',
  starter:    '#3B82F6',
  pro:        '#00D67E',
  business:   '#F59E0B',
  enterprise: '#8B5CF6',
};

const TIER_ORDER = ['free', 'starter', 'pro', 'business', 'enterprise'];

function UsageBar({ icon: Icon, label, used, limit }) {
  const pct = limit === -1 ? 0 : limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
  const color = limit === -1
    ? 'var(--color-accent)'
    : pct >= 90 ? '#EF4444'
    : pct >= 70 ? '#F59E0B'
    : 'var(--color-accent)';
  const remaining = limit === -1 ? 'Unlimited' : Math.max(0, limit - used).toLocaleString();
  const limitText = limit === -1 ? 'Unlimited' : limit.toLocaleString();

  return (
    <div className="mb-4 last:mb-0">
      <div className="mb-1.5 flex items-center justify-between text-sm">
        <span className="flex items-center gap-2" style={{ color: 'var(--color-text-secondary)' }}>
          <Icon size={13} style={{ color: 'var(--color-text-muted)' }} />
          <span className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            {used.toLocaleString()}
          </span>
          {label}
        </span>
        <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
          {limitText}
        </span>
      </div>
      <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--color-elevated)' }}>
        <div className="h-full rounded-full transition-all duration-700"
          style={{ width: `${pct}%`, background: color }} />
      </div>
      {limit > 0 && (
        <p className="mt-1 text-xs" style={{ color: 'var(--color-text-muted)' }}>
          {remaining} remaining
          {pct >= 90 && (
            <span className="ml-2 font-semibold" style={{ color: '#EF4444' }}>— upgrade to avoid interruption</span>
          )}
        </p>
      )}
    </div>
  );
}

export default function Billing() {
  const { api } = useAuth();
  const [usage, setUsage] = useState(null);
  const [plans, setPlans] = useState(null);
  const [cost, setCost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState('monthly'); // 'monthly' | 'yearly'

  useEffect(() => {
    (async () => {
      try {
        const [usageRes, plansRes, costRes] = await Promise.all([
          api().get('/api/billing/usage'),
          api().get('/api/billing/plans'),
          api().get('/api/billing/cost-summary').catch(() => ({ data: null })),
        ]);
        setUsage(usageRes.data);
        setPlans(plansRes.data);
        setCost(costRes.data);
      } catch (err) { console.error(err); }
      setLoading(false);
    })();
  }, [api]);

  useEffect(() => {
    const token = process.env.REACT_APP_PADDLE_CLIENT_TOKEN;
    if (!token || !window.Paddle) return;
    try {
      window.Paddle.Environment.set(
        process.env.REACT_APP_PADDLE_ENV === 'production' ? 'production' : 'sandbox'
      );
      window.Paddle.Initialize({ token });
    } catch (err) {
      console.error('Paddle init failed:', err);
    }
  }, []);

  const handleUpgrade = async (tier) => {
    setCheckingOut(tier);
    try {
      const idemKey = `checkout-${tier}-${billingPeriod}-${Date.now()}`;
      const res = await api().post(
        '/api/billing/checkout',
        { tier, billing_period: billingPeriod },
        { headers: { 'Idempotency-Key': idemKey } },
      );
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.detail || 'Could not start checkout. Please try again or contact sales@frontech-consulting.com');
    }
    setCheckingOut(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <FrontechLogo size={40} variant="loader" color="var(--color-accent)" />
      </div>
    );
  }

  const currentPlan   = usage?.plan || 'free';
  const limits        = usage?.limits || {};
  const usageData     = usage?.usage || {};
  const planInfo      = usage?.plan_info || {};

  const orderedPlans = plans
    ? TIER_ORDER.filter(k => plans[k]).map(k => [k, plans[k]])
    : [];

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 data-testid="billing-heading"
          className="font-heading text-2xl font-bold tracking-tight"
          style={{ color: 'var(--color-text-primary)' }}>
          Billing
        </h1>
        <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
          Manage your plan and track usage
        </p>
      </div>

      {/* Usage card */}
      <div className="rounded-2xl p-6 mb-8"
        style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
        <div className="flex items-start justify-between mb-5">
          <div>
            <p className="text-xs font-semibold tracking-widest uppercase mb-1"
              style={{ color: 'var(--color-text-muted)' }}>
              Current Usage
            </p>
            <div className="flex items-center gap-2">
              <span className="font-heading text-lg font-bold capitalize"
                style={{ color: 'var(--color-text-primary)' }}>
                {planInfo.name || currentPlan} Plan
              </span>
              {currentPlan !== 'free' && (
                <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold"
                  style={{
                    background: `${PLAN_ACCENT[currentPlan] || 'var(--color-accent)'}20`,
                    color: PLAN_ACCENT[currentPlan] || 'var(--color-accent)',
                    border: `1px solid ${PLAN_ACCENT[currentPlan] || 'var(--color-accent)'}30`,
                  }}>
                  <Star size={10} /> Active
                </span>
              )}
            </div>
          </div>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: 'var(--color-elevated)' }}>
            <CreditCard size={18} style={{ color: 'var(--color-text-muted)' }} />
          </div>
        </div>

        <UsageBar
          icon={Mic}
          label="voice minutes"
          used={usageData.voice_minutes ?? usage?.minutes_used ?? 0}
          limit={limits.voice_minutes ?? usage?.minutes_limit ?? 25}
        />
        <UsageBar
          icon={MessageSquare}
          label="chat messages"
          used={usageData.chat_messages ?? 0}
          limit={limits.chat_messages ?? 500}
        />

        {cost && (
          <div className="mt-5 pt-5 flex items-center justify-between"
            style={{ borderTop: '1px solid var(--color-border)' }}>
            <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              <DollarSign size={13} style={{ color: 'var(--color-text-muted)' }} />
              Estimated AI cost this cycle
            </div>
            <div className="text-right">
              <div className="font-heading text-lg font-bold" style={{ color: 'var(--color-text-primary)' }}>
                ${cost.estimated_cost_usd?.toFixed?.(2) ?? '0.00'}
              </div>
              <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                chat ${cost.chat_cost_usd?.toFixed?.(2) ?? '0.00'} · voice ${cost.voice_cost_usd?.toFixed?.(2) ?? '0.00'}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Billing period toggle */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs font-semibold tracking-widest uppercase"
          style={{ color: 'var(--color-text-muted)' }}>
          Available Plans
        </p>
        <div className="inline-flex rounded-lg p-0.5" style={{ background: 'var(--color-elevated)', border: '1px solid var(--color-border)' }}>
          <button
            onClick={() => setBillingPeriod('monthly')}
            className="px-3 py-1.5 text-xs font-semibold rounded-md transition-all"
            style={{
              background: billingPeriod === 'monthly' ? 'var(--color-accent)' : 'transparent',
              color: billingPeriod === 'monthly' ? '#000' : 'var(--color-text-secondary)',
            }}>
            Monthly
          </button>
          <button
            onClick={() => setBillingPeriod('yearly')}
            className="px-3 py-1.5 text-xs font-semibold rounded-md transition-all flex items-center gap-1.5"
            style={{
              background: billingPeriod === 'yearly' ? 'var(--color-accent)' : 'transparent',
              color: billingPeriod === 'yearly' ? '#000' : 'var(--color-text-secondary)',
            }}>
            Yearly
            <span className="text-[10px] px-1.5 py-0.5 rounded font-bold"
              style={{
                background: billingPeriod === 'yearly' ? '#000' : 'rgba(0,214,126,0.15)',
                color: billingPeriod === 'yearly' ? 'var(--color-accent)' : 'var(--color-accent)',
              }}>
              -17%
            </span>
          </button>
        </div>
      </div>

      {/* Plan cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        {orderedPlans.map(([key, plan]) => {
          const isActive = key === currentPlan;
          const accent   = PLAN_ACCENT[key] || 'var(--color-accent)';
          const isPro    = key === 'pro';
          const monthly  = plan.monthly_price ?? plan.price ?? 0;
          const annual   = plan.annual_price ?? 0;
          const displayPrice = billingPeriod === 'yearly' && annual > 0 ? Math.round(annual / 12) : monthly;
          const isEnterprise = key === 'enterprise';

          return (
            <div key={key} data-testid={`plan-card-${key}`}
              className="rounded-2xl p-6 flex flex-col relative overflow-hidden transition-all"
              style={{
                background: isActive ? `${accent}08` : 'var(--color-surface)',
                border: `1px solid ${isActive ? `${accent}40` : 'var(--color-border)'}`,
              }}>

              {isPro && (
                <div className="absolute top-4 right-4">
                  <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                    style={{ background: `${accent}20`, color: accent, border: `1px solid ${accent}30` }}>
                    Popular
                  </span>
                </div>
              )}

              {isActive && (
                <span className="text-xs font-mono font-semibold tracking-widest uppercase mb-3 block"
                  style={{ color: accent }}>
                  ✓ Current Plan
                </span>
              )}

              <h3 className="font-heading text-lg font-bold mb-1"
                style={{ color: 'var(--color-text-primary)' }}>
                {plan.name}
              </h3>

              <div className="mb-5">
                <span className="font-heading text-3xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
                  {monthly === 0 && !isEnterprise ? 'Free' : isEnterprise ? `from $${monthly}` : `$${displayPrice}`}
                </span>
                {monthly > 0 && (
                  <span className="text-sm ml-1" style={{ color: 'var(--color-text-muted)' }}>/month</span>
                )}
                {billingPeriod === 'yearly' && annual > 0 && monthly > 0 && (
                  <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                    ${annual} billed annually
                  </p>
                )}
              </div>

              <ul className="space-y-2 mb-6 flex-1">
                {(plan.features || []).map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm"
                    style={{ color: 'var(--color-text-secondary)' }}>
                    <Check size={13} style={{ color: accent, flexShrink: 0, marginTop: 4 }} />
                    {f}
                  </li>
                ))}
              </ul>

              {isActive ? (
                <div className="text-center py-2.5 text-sm rounded-xl font-medium"
                  style={{ background: 'var(--color-elevated)', color: 'var(--color-text-muted)', border: '1px solid var(--color-border)' }}>
                  Active
                </div>
              ) : isEnterprise ? (
                <a href="mailto:sales@frontech-consulting.com"
                  data-testid={`plan-btn-${key}`}
                  className="btn-ghost w-full justify-center text-sm py-2.5">
                  Contact Sales <ArrowRight size={13} />
                </a>
              ) : key === 'free' ? (
                <div className="text-center py-2.5 text-sm rounded-xl font-medium"
                  style={{ background: 'var(--color-elevated)', color: 'var(--color-text-muted)', border: '1px solid var(--color-border)' }}>
                  Default tier
                </div>
              ) : (
                <button data-testid={`plan-btn-${key}`}
                  onClick={() => handleUpgrade(key)}
                  disabled={checkingOut !== null}
                  className="btn-accent w-full justify-center text-sm py-2.5 disabled:opacity-50">
                  <Zap size={13} /> {checkingOut === key ? 'Redirecting…' : `Upgrade to ${plan.name}`}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer note */}
      <div className="mt-6 rounded-xl px-5 py-4"
        style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
        <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
          Payments handled securely by Paddle — no card data touches our servers. For Enterprise pricing or annual deals, email{' '}
          <span style={{ color: 'var(--color-accent)' }}>sales@frontech-consulting.com</span>
        </p>
      </div>
    </div>
  );
}
